import { ActivityIndicator } from '@/components/activity-indicator';
import {
  ATTACHMENT_HTML_SEARCH_INPUT_CLICK_EVENT,
  ATTACHMENT_IMAGE_AREA_CLICK_EVENT,
  AttachmentListEMLProps,
  IMAGE_HIGHLIGHT_ERROR_EVENT,
  INLINE_IMAGE_CLICK_EVENT,
  REPORT_CONTENT_HEIGHT_EVENT,
  REPORT_HTML_HAS_NO_CONTENT_EVENT,
  UPDATE_HTML_HIGHLIGHT_SELECTION_EVENT,
  UPDATE_IMAGE_HIGHLIGHT_SELECTION_EVENT,
} from './attachment-list-types';
import {
  ATTACHMENT_HTML_SEARCH_INPUT_HIGHLIGHT_CLASS,
  ATTACHMENT_HTML_SEARCH_INPUT_UUID_DATA_ATTRIBUTE,
  ATTACHMENT_HTML_SELECTED_SEARCH_INPUT_CLASS,
  generateHTMLHighlightInjectionScript,
  generateImageHighlightInjectionScript,
  generateLinkInterceptScript,
  useHTMLHighlighElementCSSStyles,
  useParsedEmlAttachment,
} from './hooks';
import { StyleSheet, Text, View } from 'react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@/contexts/theme-context';

const IFRAME_DEFAULT_HEIGHT = 300;
const IFRAME_HEIGHT_SLACK = 50;

export const AttachmentListEML: React.FC<AttachmentListEMLProps> = ({
  emlAttachment,
  imageHighlights,
  onEmlError,
  onHtmlHasNoContent,
  onHighlightElementSelect,
  onInlineImageClick,
  onAreaSelect,
  selectedSearchInputUUID,
  showHighlights = true,
  width,
  inlineAttachments,
}) => {
  const [iframeHeight, setIframeHeight] = useState<number>(
    IFRAME_DEFAULT_HEIGHT,
  );

  const theme = useTheme();

  const { parsedEml, error, status } = useParsedEmlAttachment(emlAttachment);

  const highlightElementCSSStyles = useHTMLHighlighElementCSSStyles();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const processedHtmlContent = useMemo(() => {
    if (!parsedEml.html) {
      return null;
    }

    const heightScript = `
      <script>
        function sendHeight() {
          window.parent.postMessage({
            data: { height: document.body.scrollHeight },
            type: '${REPORT_CONTENT_HEIGHT_EVENT}',
          }, '${typeof window !== 'undefined' ? window.location.origin : '*'}');
        }
        window.addEventListener('load', sendHeight);
        window.addEventListener('resize', sendHeight);
      </script>
    `;

    const inlineImageScript = inlineAttachments
      ? `
        <script>
          (function() {
            const inlineAttachments = ${JSON.stringify(
              inlineAttachments.map((att) => ({
                inboundContentId: att.inboundContentId,
                url: att.url,
              })),
            )};

            function setupInlineImages() {
              const images = document.querySelectorAll('img[src^="cid:"]');

              images.forEach(function(img, idx) {
                const cidSrc = img.getAttribute('src');
                if (!cidSrc) return;

                const contentId = cidSrc.replace('cid:', '');

                const attachment = inlineAttachments.find(function(att) {
                  return att.inboundContentId === contentId;
                });

                if (attachment) {
                  img.src = attachment.url;
                  img.id = contentId;
                  img.style.cursor = 'pointer';

                  img.addEventListener('click', function(e) {
                    e.preventDefault();
                    window.parent.postMessage({
                      type: '${INLINE_IMAGE_CLICK_EVENT}',
                      data: { inboundContentId: contentId, index: idx }
                    }, '${typeof window !== 'undefined' ? window.location.origin : '*'}');
                  });
                }
              });
            }

            if (document.readyState === 'loading') {
              document.addEventListener('DOMContentLoaded', setupInlineImages);
            } else {
              setupInlineImages();
            }
          })();
        </script>
      `
      : '';

    const contentCheckScript = `
      <script>
        function checkHtmlContent() {
          const bodyText = (document.body.textContent || '').trim();
          const hasContent = bodyText.length > 0;
          const hasImages = document.querySelectorAll('img').length > 0;

          if (!hasContent && !hasImages) {
            window.parent.postMessage({
              type: '${REPORT_HTML_HAS_NO_CONTENT_EVENT}',
              data: { hasContent: false },
            }, '${typeof window !== 'undefined' ? window.location.origin : '*'}');
          }
        }

        window.addEventListener('load', checkHtmlContent);
      </script>
    `;

    const imageHighlightScript =
      imageHighlights && Object.keys(imageHighlights).length > 0
        ? `
          <script>
            ${generateImageHighlightInjectionScript({
              errorEvent: IMAGE_HIGHLIGHT_ERROR_EVENT,
              highlightColor: theme.colors.pepperGreen,
              imageAreaClickEvent: ATTACHMENT_IMAGE_AREA_CLICK_EVENT,
              imageHighlights: imageHighlights,
              selectedAreaID: undefined, // Will be set dynamically via updateImageHighlightSelection
              selectedColor: theme.colors.positive,
              updateSelectionEvent: UPDATE_IMAGE_HIGHLIGHT_SELECTION_EVENT,
            })}
          </script>
        `
        : '';

    const htmlHighlightScript = `
      <script>
        ${generateHTMLHighlightInjectionScript({
          clickEvent: ATTACHMENT_HTML_SEARCH_INPUT_CLICK_EVENT,
          cssStyles: showHighlights ? highlightElementCSSStyles : '',
          highlightClass: ATTACHMENT_HTML_SEARCH_INPUT_HIGHLIGHT_CLASS,
          selectedClass: ATTACHMENT_HTML_SELECTED_SEARCH_INPUT_CLASS,
          updateSelectionEvent: UPDATE_HTML_HIGHLIGHT_SELECTION_EVENT,
          uuidDataAttribute: ATTACHMENT_HTML_SEARCH_INPUT_UUID_DATA_ATTRIBUTE,
        })}
      </script>
    `;

    const linkInterceptScript = `
      <script>
        ${generateLinkInterceptScript()}
      </script>
    `;

    const extraStyles = `
    <style>
      body {
        overflow-x: auto;
      }

      ::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 8px;
        height: 8px;
      }

      ::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.5);
        border-radius: 10px;
        border: 2px solid #ffffff;
      }

      ::-webkit-scrollbar-track {
        background: #f1f1f1;
      }
    </style>
    `;

    let htmlContent = parsedEml.html;

    if (htmlContent.includes('</head>')) {
      return htmlContent.replace(
        '</head>',
        `${heightScript}${inlineImageScript}${contentCheckScript}${imageHighlightScript}${htmlHighlightScript}${linkInterceptScript}${extraStyles}</head>`,
      );
    }

    if (htmlContent.includes('<body')) {
      return htmlContent.replace(
        '<body',
        `<head>${heightScript}${inlineImageScript}${contentCheckScript}${imageHighlightScript}${htmlHighlightScript}${linkInterceptScript}${extraStyles}</head><body`,
      );
    }

    return `<head>${heightScript}${inlineImageScript}${contentCheckScript}${imageHighlightScript}${htmlHighlightScript}${linkInterceptScript}${extraStyles}</head>${htmlContent}`;
  }, [
    parsedEml.html,
    inlineAttachments,
    imageHighlights,
    showHighlights,
    theme,
    highlightElementCSSStyles,
  ]);

  useEffect(() => {
    if (status === 'error') {
      onEmlError?.();
    }
  }, [status, onEmlError]);

  // Update image highlight selection state without re-rendering the iframe
  useEffect(() => {
    if (
      !iframeRef.current ||
      !imageHighlights ||
      Object.keys(imageHighlights).length === 0
    ) {
      return;
    }

    const iframeWindow = iframeRef.current.contentWindow;
    if (iframeWindow) {
      // Type assertion needed as the function is dynamically added to the window
      const win = iframeWindow as Window & {
        updateImageHighlightSelection?: (areaId: string | null) => void;
      };
      if (typeof win.updateImageHighlightSelection === 'function') {
        win.updateImageHighlightSelection(selectedSearchInputUUID ?? null);
      }
    }
  }, [selectedSearchInputUUID, imageHighlights]);

  // Update HTML highlight selection state without re-rendering the iframe
  useEffect(() => {
    if (!iframeRef.current) {
      return;
    }

    const iframeWindow = iframeRef.current.contentWindow;
    if (iframeWindow) {
      iframeWindow.postMessage(
        {
          data: { selectedSearchInputUUID: selectedSearchInputUUID ?? null },
          type: UPDATE_HTML_HIGHLIGHT_SELECTION_EVENT,
        },
        window.location.origin,
      );
    }
  }, [selectedSearchInputUUID]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        !iframeRef.current ||
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }
      if (event.data?.type === REPORT_CONTENT_HEIGHT_EVENT) {
        const newHeight = event.data.data.height;
        if (newHeight) {
          setIframeHeight(newHeight + IFRAME_HEIGHT_SLACK);
        }
      } else if (event.data?.type === REPORT_HTML_HAS_NO_CONTENT_EVENT) {
        onHtmlHasNoContent?.();
      } else if (event.data?.type === INLINE_IMAGE_CLICK_EVENT) {
        onInlineImageClick?.(
          event.data.data.inboundContentId,
          event.data.data.index,
        );
      } else if (event.data?.type === ATTACHMENT_IMAGE_AREA_CLICK_EVENT) {
        const { areaId, imageUrl } = event.data.data;
        if (areaId && onAreaSelect) {
          // Find the area in imageHighlights
          const areas = imageHighlights?.[imageUrl];
          const area = areas?.find((a) => a.id === areaId);
          if (area) {
            onAreaSelect(area);
          }
        }
      } else if (
        event.data?.type === ATTACHMENT_HTML_SEARCH_INPUT_CLICK_EVENT
      ) {
        const searchInputUUID = event.data.data.searchInputUUID;
        if (searchInputUUID) {
          onHighlightElementSelect?.(searchInputUUID);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [
    onHtmlHasNoContent,
    onInlineImageClick,
    onAreaSelect,
    onHighlightElementSelect,
    imageHighlights,
  ]);

  switch (status) {
    case 'loading':
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator testID="activity-indicator" />
        </View>
      );
    case 'error':
      return (
        <Text>
          Error:{' '}
          {error?.message ??
            'An error has occurred while trying to display your email content'}
        </Text>
      );
    default: {
      if (!parsedEml.html) {
        return parsedEml.text ? <Text>{parsedEml.text}</Text> : null;
      }

      return (
        <iframe
          allow=""
          data-testid="eml-iframe"
          ref={iframeRef}
          referrerPolicy="no-referrer"
          sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
          srcDoc={processedHtmlContent ?? ''}
          style={StyleSheet.flatten([
            styles.iframeContainer,
            {
              height: iframeHeight,
              width,
            },
          ])}
        />
      );
    }
  }
};

const styles = StyleSheet.create({
  iframeContainer: {
    borderWidth: 0,
    padding: 0,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
});
